/**
 * RetellPage — «Пересказ близко к тексту» (раздел «Связная речь»).
 *
 * На экране РАССКАЗ целиком (этап чтения), затем пересказ ВСЛУХ — счёт
 * считает сервер по эталону рассказа (cortivox.retell_milestones: вехи,
 * связки «A + B», синонимы, ASR-варианты). Ключи и формулировки вех на
 * клиент НЕ уходят: пациент видит только текст рассказа и наводящие вопросы.
 *
 * Сценарий:
 *   1. ВЫБОР РАССКАЗА (t01..t04 — банк retell_stories; остальные 16 — после
 *      обкатки). ?story=t04 открывает рассказ сразу.
 *   2. ЧТЕНИЕ: врач читает рассказ пациенту вслух (или пациент читает сам).
 *      МИКРОФОН НА ЭТОМ ЭТАПЕ НЕ ВКЛЮЧАЕТСЯ (не пишем чтение в транскрипт!) —
 *      это осознанное отличие от «Беглости»/«Зрительного восприятия», где
 *      запись стартует сразу.
 *   3. ПЕРЕСКАЗ: кнопка «Начать пересказ» запрашивает микрофон и включает
 *      распознавание (ru-RU, continuous; Chrome останавливается на паузах —
 *      перезапускаем в onend). Кнопка «Подсказка» — наводящий вопрос по
 *      СЛЕДУЮЩЕЙ вехе в порядке текста (закрытость вех клиент не знает —
 *      ключи на сервере; темп подсказок ведёт врач). Лимит подсказок —
 *      config.hintLimit (эталон: 8–12). Мягкий лимит времени —
 *      config.softLimitSec (по умолчанию 5 минут на пересказ).
 *   4. Финиш («Готово» или мягкий лимит) → transcript + протокол уходят на
 *      POST /api/trainers/retell {stepId: 'r01'..'r04', elapsedMs,
 *      thinkingMs, hintsUsed} — сервер считает вехи/блоки/флаги и пишет
 *      отчёт в ОБЩУЮ таблицу trainer_reports (algorithm_version='retell-web-1').
 *   5. РЕЗУЛЬТАТ: процент ядра, «Названо X из N» по блокам, флаги причинных
 *      цепочек, список «частично». Врач дополнительно видит таблицу вех
 *      (формулировка, статус, чем зачтено) и «Детали для врача» — сверка
 *      зачёта по стенограмме (правило «Обнаружение ≠ оценка» эталона).
 *
 * Маршрут: /retell — пациент сам; /retell?patientId=UUID — запуск врачом из
 * карточки пациента; /retell?story=t04&patientId=… — сразу к рассказу.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  BrainCircuit,
  CheckCircle2,
  CircleDashed,
  Flag,
  Lightbulb,
  Loader2,
  Mic,
  MicOff,
  RotateCcw,
} from 'lucide-react';
// Клиент — общий cortivoxApi.js (тот же request-хелпер с Bearer-токеном).
import {
  getAuthUserId,
  getRetellStories,
  getRetellStory,
  localToday,
  saveRetellReport,
} from '../lib/cortivoxApi';
import { createRecognition, isRecognitionSupported } from '../services/speechRecognition';

const MAX_WORDS = 800;                       // защита от аномально длинного transcript
const DEFAULT_SOFT_LIMIT_MS = 300_000;       // 5 минут на пересказ, если сервер не задал
const DEFAULT_HINT_LIMIT = 10;               // лимит подсказок 8–12 (эталон)

const stepIdForStory = (num) => `r${String(Number(num) || 1).padStart(2, '0')}`;

const fmtElapsed = (ms) => {
  const s = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const STATUS = {
  closed: { icon: CheckCircle2, text: 'закрыта', cls: 'text-emerald-600' },
  partial: { icon: CircleDashed, text: 'частично', cls: 'text-amber-600' },
  missed: { icon: CircleDashed, text: 'не воспроизведена', cls: 'text-cvx-muted-foreground' },
};

const hintFromError = (err) => {
  const status = err?.status ?? err?.response?.status;
  if (status === 403 || status === 404) {
    return 'Если вы врач: открывайте упражнение из карточки пациента — /retell?patientId=UUID пациента.';
  }
  return null;
};

export default function RetellPage() {
  // phases: select | reading | running | saving | done | error
  const [phase, setPhase] = useState('select');
  const [stories, setStories] = useState(null);
  const [session, setSession] = useState(null);
  const [result, setResult] = useState(null);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [errorHint, setErrorHint] = useState('');
  const [patientId, setPatientId] = useState(null);
  const [doctorMode, setDoctorMode] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [listening, setListening] = useState(false);
  const [lastHint, setLastHint] = useState(null);   // {num, question} текущей подсказки
  const [hintPresses, setHintPresses] = useState(0);

  // --- Web Speech API -------------------------------------------------------
  const recognitionRef = useRef(null);
  const runningRef = useRef(false);
  const finalsRef = useRef([]);
  const interimRef = useRef('');
  const startedAtRef = useRef(0);
  const firstSpeechAtRef = useRef(0);
  const finishedRef = useRef(false);
  const softLimitRef = useRef(0);

  // --- подсказки -------------------------------------------------------------
  const hintIdxRef = useRef(0);      // следующая веха, по которой ещё не давали вопрос

  const supported = useMemo(() => isRecognitionSupported(), []);

  const hintLimit = Number(session?.config?.hintLimit) > 0
    ? Number(session.config.hintLimit) : DEFAULT_HINT_LIMIT;
  const softLimitMs = Number(session?.config?.softLimitSec) > 0
    ? Number(session.config.softLimitSec) * 1000 : DEFAULT_SOFT_LIMIT_MS;
  const milestones = useMemo(
    () => (Array.isArray(session?.milestones) ? session.milestones : []), [session]);

  const stopRecognition = useCallback(() => {
    runningRef.current = false;
    window.clearTimeout(softLimitRef.current);
    setListening(false);
    try {
      recognitionRef.current?.stop();
    } catch { /* уже остановлен — не страшно */ }
    recognitionRef.current = null;
  }, []);

  const resetRunState = useCallback(() => {
    setResult(null); setFinalTranscript(''); setLiveText('');
    setLastHint(null); setHintPresses(0);
    hintIdxRef.current = 0;
  }, []);

  const openStory = useCallback(async (code, uid = patientId) => {
    setErrorMsg(''); setErrorHint('');
    resetRunState();
    try {
      const data = await getRetellStory(code);
      setSession(data);
      setPhase('reading');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось загрузить рассказ.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [patientId, resetRunState]);

  const loadStories = useCallback(async () => {
    setPhase('select');
    setErrorMsg(''); setErrorHint('');
    try {
      const params = new URLSearchParams(window.location.search);
      const pidOverride = params.get('patientId');
      setDoctorMode(Boolean(pidOverride));
      const uid = pidOverride || (await getAuthUserId());
      if (!uid) {
        setErrorMsg('Не удалось определить пользователя. Войдите в аккаунт и откройте упражнение заново.');
        setPhase('error');
        return;
      }
      setPatientId(uid);
      const res = await getRetellStories();
      const list = Array.isArray(res) ? res : res?.items || [];
      setStories(list);
      // ?story=t04 — сразу открыть рассказ (врач запускает конкретный).
      const want = String(params.get('story') || '').trim().toLowerCase();
      const found = list.find((s) => String(s.code || '').toLowerCase() === want);
      if (found) await openStory(found.code, uid);
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось загрузить список рассказов.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [openStory]);

  // Отправка transcript на сервер (счёт считает сервер).
  const finishRun = useCallback(async () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    stopRecognition();
    const raw = [...finalsRef.current, interimRef.current].filter(Boolean).join(' ');
    const transcript = raw.replace(/\s+/g, ' ').trim().split(' ')
      .filter(Boolean).slice(0, MAX_WORDS).join(' ');
    setFinalTranscript(transcript);
    const elapsedMs = startedAtRef.current ? Date.now() - startedAtRef.current : null;
    // «На раздумье»: от старта пересказа до первого распознанного слова.
    const thinkingMs = startedAtRef.current
      ? (firstSpeechAtRef.current
        ? Math.max(0, firstSpeechAtRef.current - startedAtRef.current)
        : elapsedMs)
      : null;

    setPhase('saving');
    try {
      const res = await saveRetellReport({
        patientId,
        stepId: stepIdForStory(session?.num),
        transcript,
        elapsedMs,
        thinkingMs,
        hintsUsed: hintPresses,
        today: localToday(),
      });
      setResult(res);
      setPhase('done');
    } catch (err) {
      setErrorMsg(err?.message || 'Не удалось сохранить результат.');
      setErrorHint(hintFromError(err) || '');
      setPhase('error');
    }
  }, [patientId, session, hintPresses, stopRecognition]);

  const finishRef = useRef(finishRun);
  useEffect(() => { finishRef.current = finishRun; }, [finishRun]);

  useEffect(() => {
    void loadStories();
  }, [loadStories]);

  // СТАРТ ПЕРЕСКАЗА: микрофон (по касанию — пользователь уже читал рассказ).
  const startRun = useCallback(async () => {
    try {
      if (navigator.mediaDevices?.getUserMedia) {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
      }
    } catch {
      setErrorMsg('Нет доступа к микрофону. Разрешите доступ к микрофону в настройках браузера и попробуйте ещё раз.');
      setPhase('error');
      return;
    }
    const rec = createRecognition({ lang: 'ru-RU', continuous: true, interimResults: true });
    if (!rec) {
      setErrorMsg('Браузер не поддерживает распознавание речи. Откройте упражнение в Google Chrome или Яндекс.Браузере.');
      setPhase('error');
      return;
    }

    finalsRef.current = [];
    interimRef.current = '';
    firstSpeechAtRef.current = 0;
    finishedRef.current = false;
    setLiveText('');
    startedAtRef.current = Date.now();
    runningRef.current = true;
    recognitionRef.current = rec;

    rec.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const res = event.results[i];
        const text = String(res?.[0]?.transcript || '').trim();
        if (!text) continue;
        if (!firstSpeechAtRef.current) firstSpeechAtRef.current = Date.now();
        if (res.isFinal) finalsRef.current.push(text);
        else interim += `${text} `;
      }
      interimRef.current = interim.trim();
      setLiveText([...finalsRef.current, interimRef.current].filter(Boolean).join(' '));
    };

    rec.onerror = (event) => {
      const code = event?.error || '';
      if (code === 'not-allowed' || code === 'service-not-allowed') {
        finishedRef.current = true;
        setErrorMsg('Нет доступа к микрофону. Разрешите доступ (значок в адресной строке) и попробуйте ещё раз.');
        setPhase('error');
      }
      // no-speech / aborted / network — переживаем: onend перезапустит.
    };

    rec.onend = () => {
      if (!runningRef.current) return;
      window.setTimeout(() => {
        if (!runningRef.current) return;
        try { rec.start(); } catch { /* уже запущен — не страшно */ }
      }, 300);
    };

    try { rec.start(); } catch { /* уже запущен — не страшно */ }
    setListening(true);
    setPhase('running');

    // Мягкий лимит: страховка, если пациент замолчал и врач не нажал «Готово».
    window.clearTimeout(softLimitRef.current);
    softLimitRef.current = window.setTimeout(() => finishRef.current(), softLimitMs);
  }, [softLimitMs]);

  const startRunRef = useRef(startRun);
  useEffect(() => { startRunRef.current = startRun; }, [startRun]);

  // ПОДСКАЗКА: наводящий вопрос по следующей вехе в порядке текста.
  // Закрытость вех клиент не знает (ключи на сервере) — подсказки идут
  // по порядку рассказа; каждое нажатие логируется в протокол.
  const giveHint = useCallback(() => {
    if (!milestones.length) return;
    if (hintIdxRef.current >= milestones.length) {
      setLastHint({ num: null, question: 'Подсказки по всем вехам рассказа уже показаны.' });
      return;
    }
    const m = milestones[hintIdxRef.current];
    hintIdxRef.current += 1;
    setHintPresses((n) => n + 1);
    setLastHint({ num: m.num, question: m.hintQuestion || `Веха №${m.num}` });
  }, [milestones]);

  // Предупреждение при случайном закрытии вкладки посреди пересказа.
  useEffect(() => {
    if (phase !== 'running') return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  const backToSelect = useCallback(() => {
    stopRecognition();
    resetRunState();
    setSession(null);
    setPhase('select');
  }, [stopRecognition, resetRunState]);

  const retellAgain = useCallback(() => {
    stopRecognition();
    resetRunState();
    setPhase('reading');
  }, [stopRecognition, resetRunState]);

  // Размонтирование: остановить микрофон.
  useEffect(() => () => {
    runningRef.current = false;
    window.clearTimeout(softLimitRef.current);
    try { recognitionRef.current?.stop(); } catch { /* ignore */ }
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
  }, []);

  // ── рендер ────────────────────────────────────────────────────────────────
  const sr = result?.scoreResult || {};
  const blocks = Array.isArray(sr.blocks) ? sr.blocks : [];
  const flags = Array.isArray(sr.flags) ? sr.flags : [];
  const msResults = Array.isArray(sr.milestones) ? sr.milestones : [];
  const doctorInfo = session?.doctorInfo || null;
  const wordCount = liveText ? liveText.trim().split(/\s+/).length : 0;

  return (
    <div className="flex min-h-screen flex-col bg-cvx-background px-4 py-6">
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col">
        <header className="flex items-center justify-between gap-3">
          <a href="/" aria-label="Cortivox.ai на главную" className="flex items-center gap-2.5">
            <span className="flex size-8 items-center justify-center rounded-[10px] bg-cvx-ink text-white">
              <BrainCircuit className="size-4" />
            </span>
            <span className="text-[16px] font-semibold tracking-[-0.04em] text-cvx-ink">
              Cortivox<span className="text-cvx-logo-ai">.ai</span>
            </span>
          </a>
          <button type="button" onClick={() => window.history.back()} className="cvx-back">
            <ArrowLeft className="size-4" /> Назад
          </button>
        </header>

        {phase === 'select' && (
          <section className="mt-6 flex flex-1 flex-col">
            <h1 className="text-2xl font-bold tracking-tight text-cvx-foreground">Пересказ близко к тексту</h1>
            <p className="mt-1.5 text-sm leading-relaxed text-cvx-muted-foreground">
              Прочитайте рассказ вслух, затем попросите пациента пересказать его своими словами.
              Сейчас в банке {stories ? stories.length : '…'} из 20 рассказов.
            </p>
            {stories == null && (
              <div className="mt-10 flex justify-center text-cvx-muted-foreground">
                <Loader2 className="size-6 animate-spin" />
              </div>
            )}
            {Array.isArray(stories) && stories.length === 0 && (
              <p className="mt-6 rounded-xl bg-cvx-muted p-4 text-sm text-cvx-muted-foreground">
                Рассказы ещё не добавлены в банк (retell_stories).
              </p>
            )}
            <div className="mt-5 flex flex-col gap-3">
              {(stories || []).map((s) => (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => openStory(s.code)}
                  className="cv-shadow rounded-2xl border border-cvx-border bg-cvx-card p-5 text-left transition hover:border-cvx-primary/40"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-cvx-muted-foreground">
                        {s.author}
                      </p>
                      <h3 className="mt-0.5 truncate text-lg font-bold text-cvx-foreground">«{s.title}»</h3>
                    </div>
                    <BookOpen className="size-5 shrink-0 text-cvx-primary" />
                  </div>
                  <p className="mt-2 text-xs text-cvx-muted-foreground">
                    вех: {Number(s.coreTotal) || 0} ядро
                    {Number(s.detailTotal) ? ` + ${s.detailTotal} деталь` : ''}
                    {' · '}блоков: {(Array.isArray(s.blocks) ? s.blocks : []).length || '—'}
                  </p>
                </button>
              ))}
            </div>
          </section>
        )}

        {phase === 'reading' && session && (
          <section className="mt-6 flex flex-1 flex-col">
            <p className="text-xs font-semibold uppercase tracking-wide text-cvx-muted-foreground">
              {session.author}
            </p>
            <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-cvx-foreground">«{session.title}»</h1>
            <p className="mt-1.5 text-sm leading-relaxed text-cvx-muted-foreground">
              Прочитайте рассказ вслух. Когда закончите — нажмите
              «Начать пересказ»: включится микрофон и пациент пересказывает своими словами.
            </p>
            <div className="cv-shadow mt-4 max-h-[52vh] overflow-y-auto rounded-2xl border border-cvx-border bg-cvx-card p-5">
              <p className="whitespace-pre-line text-[15px] leading-relaxed text-cvx-foreground">
                {session.storyText}
              </p>
            </div>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => startRunRef.current()}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cvx-primary px-5 py-3.5 font-semibold text-white transition hover:opacity-90"
              >
                <Mic className="size-5" /> Начать пересказ
              </button>
              <button
                type="button"
                onClick={backToSelect}
                className="rounded-xl border border-cvx-border px-5 py-3.5 font-semibold text-cvx-foreground transition hover:bg-cvx-muted"
              >
                Другой рассказ
              </button>
            </div>
          </section>
        )}

        {(phase === 'running' || phase === 'saving') && session && (
          <section className="mt-6 flex flex-1 flex-col">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {listening ? (
                  <span className="flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-sm font-semibold text-red-600">
                    <span className="relative flex size-2.5">
                      <span className="absolute inline-flex size-full animate-ping rounded-full bg-red-400" />
                      <span className="relative inline-flex size-2.5 rounded-full bg-red-500" />
                    </span>
                    Идёт запись
                  </span>
                ) : (
                  <span className="flex items-center gap-2 rounded-full bg-cvx-muted px-3 py-1 text-sm text-cvx-muted-foreground">
                    <MicOff className="size-3.5" /> микрофон выключен
                  </span>
                )}
              </div>
              <p className="text-sm tabular-nums text-cvx-muted-foreground">{wordCount} слов</p>
            </div>

            <h2 className="mt-3 text-lg font-bold text-cvx-foreground">«{session.title}»</h2>

            <div className="cv-shadow mt-3 max-h-36 overflow-y-auto rounded-2xl border border-cvx-border bg-cvx-card p-4">
              <p className="min-h-12 text-[15px] leading-relaxed text-cvx-foreground">
                {liveText || <span className="text-cvx-muted-foreground">Пересказ появится здесь по мере речи…</span>}
              </p>
            </div>

            {lastHint && (
              <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 p-4">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-700">
                  <Lightbulb className="size-4" />
                  Подсказка{lastHint.num ? ` · веха ${lastHint.num}` : ''}
                </p>
                <p className="mt-1 text-[15px] font-medium leading-relaxed text-amber-900">
                  {lastHint.question}
                </p>
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={giveHint}
                disabled={phase !== 'running' || hintPresses >= hintLimit}
                className="flex items-center justify-center gap-2 rounded-xl border border-cvx-border bg-cvx-card px-5 py-3.5 font-semibold text-cvx-foreground transition hover:bg-cvx-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Lightbulb className="size-5" />
                Подсказка{hintPresses > 0 ? ` (${hintPresses}/${hintLimit})` : ''}
              </button>
              <button
                type="button"
                onClick={() => finishRef.current()}
                disabled={phase !== 'running'}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cvx-primary px-5 py-3.5 font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {phase === 'saving'
                  ? <Loader2 className="size-5 animate-spin" />
                  : <CheckCircle2 className="size-5" />}
                {phase === 'saving' ? 'Считаем…' : 'Готово'}
              </button>
            </div>
            {hintPresses >= hintLimit && (
              <p className="mt-2 text-xs text-amber-700">
                Достигнут лимит подсказок для этого пересказа ({hintLimit}).
              </p>
            )}
          </section>
        )}

        {phase === 'done' && result && (
          <section className="mt-6 flex flex-1 flex-col">
            <h1 className="text-2xl font-bold tracking-tight text-cvx-foreground">Пересказ сохранён</h1>
            <p className="mt-1 text-sm text-cvx-muted-foreground">
              «{result.title}» · {fmtElapsed(sr.elapsedMs)}
              {sr.thinkingMs != null ? ` · на раздумье ${fmtElapsed(sr.thinkingMs)}` : ''}
              {` · подсказок: ${Number(sr.hintsUsed) || 0}`}
            </p>

            <div className="cv-shadow mt-4 rounded-2xl border border-cvx-border bg-cvx-card p-5 text-center">
              <p className="text-5xl font-bold leading-none tabular-nums text-cvx-primary">
                {Number(sr.percent) || 0}%
              </p>
              <p className="mt-2 text-sm text-cvx-muted-foreground">
                ядро: {Number(sr.coreClosed) || 0} из {Number(sr.coreTotal) || 0} вех
                {Number(sr.detailTotal) > 0
                  ? ` · детали: ${Number(sr.detailClosed) || 0}/${sr.detailTotal}`
                  : ''}
              </p>
            </div>

            {blocks.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-cvx-foreground">По блокам сюжета</p>
                <div className="mt-2 flex flex-col gap-1.5">
                  {blocks.map((b) => (
                    <div key={b.name} className="flex items-center gap-3">
                      <span className="w-32 shrink-0 truncate text-xs text-cvx-muted-foreground">{b.name}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-cvx-muted">
                        <div
                          className="h-full rounded-full bg-cvx-primary"
                          style={{ width: `${b.coreTotal ? Math.round((b.coreClosed / b.coreTotal) * 100) : 0}%` }}
                        />
                      </div>
                      <span className="w-10 shrink-0 text-right text-xs tabular-nums text-cvx-muted-foreground">
                        {b.coreClosed}/{b.coreTotal}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {flags.length > 0 && (
              <div className="mt-4 flex flex-col gap-2">
                {flags.map((f) => (
                  <div key={f.type} className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3.5">
                    <Flag className="mt-0.5 size-4 shrink-0 text-amber-600" />
                    <p className="text-sm leading-relaxed text-amber-900">
                      <span className="font-semibold">{f.label || 'Флаг'}. </span>
                      {f.message}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {doctorMode && doctorInfo && (
              <div className="mt-5">
                <p className="text-sm font-semibold text-cvx-foreground">Вехи (сверка по стенограмме)</p>
                <div className="mt-2 flex max-h-80 flex-col gap-1.5 overflow-y-auto rounded-2xl border border-cvx-border bg-cvx-card p-3">
                  {msResults.map((m) => {
                    const st = STATUS[m.status] || STATUS.missed;
                    const info = doctorInfo[String(m.num)] || {};
                    const StIcon = st.icon;
                    return (
                      <div key={m.num} className="rounded-xl bg-cvx-muted/60 p-3">
                        <p className="flex items-start gap-2 text-sm">
                          <StIcon className={`mt-0.5 size-4 shrink-0 ${st.cls}`} />
                          <span className="text-cvx-foreground">
                            <span className="font-semibold">№{m.num}</span>{' '}
                            {info.formulation || ''}
                            {info.level === 'деталь' && (
                              <span className="ml-1 rounded bg-cvx-secondary px-1.5 py-0.5 text-[10px] font-semibold text-cvx-secondary-foreground">деталь</span>
                            )}
                          </span>
                        </p>
                        {m.matchedBy && (
                          <p className="ml-6 mt-1 text-xs text-cvx-muted-foreground">зачтено: {m.matchedBy}</p>
                        )}
                        {info.details && m.status !== 'closed' && (
                          <p className="ml-6 mt-1 text-xs leading-relaxed text-cvx-muted-foreground">
                            детали: {info.details}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {finalTranscript && (
              <details className="mt-4 rounded-2xl border border-cvx-border bg-cvx-card p-4">
                <summary className="cursor-pointer text-sm font-semibold text-cvx-foreground">
                  Стенограмма пересказа
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-cvx-muted-foreground">{finalTranscript}</p>
              </details>
            )}

            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={retellAgain}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-cvx-primary px-5 py-3.5 font-semibold text-white transition hover:opacity-90"
              >
                <RotateCcw className="size-5" /> Пересказать ещё раз
              </button>
              <button
                type="button"
                onClick={backToSelect}
                className="rounded-xl border border-cvx-border px-5 py-3.5 font-semibold text-cvx-foreground transition hover:bg-cvx-muted"
              >
                Другой рассказ
              </button>
            </div>
          </section>
        )}

        {phase === 'error' && (
          <section className="mt-10 flex flex-1 flex-col items-center text-center">
            <AlertTriangle className="size-10 text-amber-500" />
            <h1 className="mt-3 text-xl font-bold text-cvx-foreground">Что-то пошло не так</h1>
            <p className="mt-2 text-sm leading-relaxed text-cvx-muted-foreground">{errorMsg}</p>
            {errorHint && <p className="mt-1 text-xs text-cvx-muted-foreground">{errorHint}</p>}
            <button
              type="button"
              onClick={() => { backToSelect(); void loadStories(); }}
              className="mt-6 rounded-xl bg-cvx-primary px-5 py-3 font-semibold text-white transition hover:opacity-90"
            >
              К списку рассказов
            </button>
          </section>
        )}

        {!supported && (phase === 'reading' || phase === 'running') && (
          <p className="mt-3 rounded-xl bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
            Браузер не поддерживает распознавание речи — пересказ можно ввести вручную на этапе
            результата (стенограмма) или открыть упражнение в Google Chrome / Яндекс.Браузере.
          </p>
        )}
      </main>
    </div>
  );
}
